import * as React from 'react'
import { CaretRight } from '@phosphor-icons/react'
import { Link } from './link'

/**
 * Breadcrumbs — shows the current page's location in the hierarchy. Design
 * adopted from the TailGrids breadcrumbs model (the system our Figma is built
 * on), wired to First Bite tokens.
 *
 *   items        BreadcrumbItem[]            href · label · optional icon
 *   dividerType  slash | chevron | dot       separator between items
 *   size         md (14px) | sm (12px)       trail text + icon scale
 *
 * Semantic `<nav aria-label="Breadcrumb"> > <ol>`. Parent items render as the DS
 * `Link` (variant `muted`: `gray-500` → `gray-700` on hover, shared focus ring,
 * no underline); the last item is the current page — non-link `gray-900` text
 * with `aria-current="page"`.
 */

type BreadcrumbsSize = 'sm' | 'md'

export interface BreadcrumbItem {
  href: string
  label: string
  /** Optional icon before the label (auto-sized per `size`, inherits color). */
  icon?: React.ReactNode
}

export interface BreadcrumbsProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  'children'
> {
  items: BreadcrumbItem[]
  dividerType?: 'slash' | 'chevron' | 'dot'
  size?: BreadcrumbsSize
}

/** Per-size class sets, kept in sync with the `Link` size scale. */
const sizeStyles: Record<
  BreadcrumbsSize,
  { ol: string; linkSize: 'xs' | 'sm'; currentGlyph: string; chevron: string }
> = {
  md: {
    ol: 'text-sm',
    linkSize: 'sm',
    currentGlyph: '[&>svg]:size-4',
    chevron: 'size-3.5',
  },
  sm: {
    ol: 'text-xs',
    linkSize: 'xs',
    currentGlyph: '[&>svg]:size-3.5',
    chevron: 'size-3',
  },
}

const Divider = ({
  type,
  chevron,
}: {
  type: 'slash' | 'chevron' | 'dot'
  chevron: string
}) => (
  <span aria-hidden="true" className="text-fbui-gray-300 select-none">
    {type === 'chevron' ? (
      <CaretRight weight="bold" className={chevron} />
    ) : type === 'dot' ? (
      <span className="inline-block size-1 rounded-full bg-fbui-gray-300 align-middle" />
    ) : (
      '/'
    )}
  </span>
)

export const Breadcrumbs = React.forwardRef<HTMLElement, BreadcrumbsProps>(
  ({ className, items, dividerType = 'slash', size = 'md', ...props }, ref) => {
    const s = sizeStyles[size]
    return (
      <nav ref={ref} aria-label="Breadcrumb" className={className} {...props}>
        <ol
          className={`flex flex-wrap items-center gap-2 tracking-[-0.2px] ${s.ol}`}
        >
          {items.map((item, i) => {
            const isLast = i === items.length - 1
            return (
              <li key={`${item.href}-${i}`} className="flex items-center gap-2">
                {isLast ? (
                  <span
                    aria-current="page"
                    className={`inline-flex items-center gap-1.5 font-medium text-fbui-gray-900 ${s.currentGlyph}`}
                  >
                    {item.icon}
                    {item.label}
                  </span>
                ) : (
                  <Link
                    href={item.href}
                    variant="muted"
                    size={s.linkSize}
                    leftIcon={item.icon}
                  >
                    {item.label}
                  </Link>
                )}
                {!isLast && <Divider type={dividerType} chevron={s.chevron} />}
              </li>
            )
          })}
        </ol>
      </nav>
    )
  }
)
Breadcrumbs.displayName = 'Breadcrumbs'
